import React, { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { MemberFormFields } from "./MemberFormFields";
import { useDebouncedEffect } from "../hooks/useDebouncedEffect";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { api, photoUrl } from "../lib/api";
import { copyText } from "../lib/clipboard";
import { toast } from "sonner";
import { User, Pencil, Trash2, ImagePlus, UserPlus, Users, Heart, Baby, MapPin, Home, AlertTriangle, Check, Loader2, Clapperboard, Share2 } from "lucide-react";
import { AnalyzeNotesPanel } from "./AnalyzeNotesPanel";
import { isEstimatedDate } from "./PersonCard";
import { PhotoGallery } from "./PhotoGallery";

function years(m) {
  const y = (s) => (String(s || "").match(/\d{4}/) || [""])[0];
  const b = y(m.birth_date), d = y(m.death_date);
  if (!b && !d) return "";
  return `${b || "?"}${d ? " – " + d : ""}`;
}

const RelChip = ({ person, onSelect }) => (
  <button
    onClick={() => onSelect(person.id)}
    data-testid={`related-chip-${person.id}`}
    className="flex items-center gap-2 rounded-full border border-[#D8E0E8] bg-white py-1 pl-1 pr-3 text-sm transition-colors hover:bg-[#E8EEF3]"
  >
    <span className="h-7 w-7 overflow-hidden rounded-full bg-[#E8EEF3]">
      {photoUrl(person.photo_path) ? (
        <img src={photoUrl(person.photo_path)} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-[#94A3B8]"><User size={14} /></span>
      )}
    </span>
    {person.full_name}
  </button>
);

export function MemberSheet({ member, members, open, onOpenChange, onAddRelative, onChanged, onSelect, onPlayStory, canEdit = true, canSuggest = false, homeId = null, onSetHome, onRequireLogin, onRequestContribute }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [editStatus, setEditStatus] = useState("idle");
  const canModify = canEdit || canSuggest;

  useEffect(() => {
    setEditing(false);
    setForm(member || {});
    setEditStatus("idle");
  }, [member?.id]);

  const EDIT_FIELDS = ["full_name", "nickname", "first_name", "middle_name", "last_name", "former_name", "gender", "birth_date", "death_date", "bio", "origin_place", "current_place"];

  // Auto-save while editing (debounced)
  useDebouncedEffect(() => {
    if (!editing || !canEdit || !member || !form.full_name?.trim()) return;
    setEditStatus("saving");
    api.update(member.id, Object.fromEntries(EDIT_FIELDS.map((k) => [k, form[k] ?? ""])))
      .then(() => { setEditStatus("saved"); onChanged?.(); })
      .catch(() => setEditStatus("idle"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(EDIT_FIELDS.map((k) => form[k])), editing], 900);

  if (!member) return null;

  const byId = Object.fromEntries(members.map((m) => [m.id, m]));
  const parents = (member.parent_ids || []).map((id) => byId[id]).filter(Boolean);
  const spouses = (member.spouse_ids || []).map((id) => byId[id]).filter(Boolean);
  const children = members.filter((m) => (m.parent_ids || []).includes(member.id));
  const siblingIds = new Set();
  parents.forEach((p) =>
    members.forEach((m) => {
      if (m.id !== member.id && (m.parent_ids || []).includes(p.id)) siblingIds.add(m.id);
    })
  );
  const siblings = [...siblingIds].map((id) => byId[id]).filter(Boolean);

  const spouseIdSet = new Set(member.spouse_ids || []);
  const stepchildren = members.filter(
    (m) => m.id !== member.id && (m.parent_ids || []).some((p) => spouseIdSet.has(p)) && !(m.parent_ids || []).includes(member.id)
  );

  const adoptStepchildren = async () => {
    try {
      const res = await api.adoptSpouseChildren(member.id);
      toast.success(res.adopted ? `Adopted ${res.adopted} ${res.adopted === 1 ? "child" : "children"}` : "No children to adopt");
      onChanged?.();
    } catch {
      toast.error("Could not adopt children");
    }
  };

  const saveEdit = async () => {
    try {
      const res = await api.update(member.id, Object.fromEntries(EDIT_FIELDS.map((k) => [k, form[k] ?? ""])));
      toast.success(res?.pending ? (res.message || "Suggestion sent for review") : "Details updated");
      setEditing(false);
      onChanged?.();
    } catch {
      toast.error("Could not save");
    }
  };

  const doDelete = async () => {
    try {
      const res = await api.remove(member.id);
      toast.success(res?.pending ? (res.message || "Removal suggested — an admin will review it") : `${member.full_name} removed`);
      onOpenChange(false);
      onChanged?.();
    } catch {
      toast.error("Could not delete");
    }
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      await api.uploadPhoto(member.id, f);
      toast.success("Photo updated");
      onChanged?.();
    } catch {
      toast.error("Could not upload photo");
    }
  };

  const sharePerson = async () => {
    const url = `${window.location.origin}/api/share/person/${member.id}`;
    const ok = await copyText(url);
    ok ? toast.success(`Link to ${member.full_name?.split(" ")[0] || "them"} copied — it shows a photo preview when shared`) : toast.error("Could not copy the link");
  };

  const relBtns = [
    { rel: "parent", label: "Parent", icon: Users, testid: "add-parent-button" },
    { rel: "spouse", label: "Spouse", icon: Heart, testid: "add-spouse-button" },
    { rel: "child", label: "Child", icon: Baby, testid: "add-child-button" },
    { rel: "sibling", label: "Sibling", icon: UserPlus, testid: "add-sibling-button" },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto bg-[#F4F6F8] sm:max-w-md" data-testid="member-details-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="sr-only">Member details</SheetTitle>
        </SheetHeader>

        <div className="mt-2 flex flex-col items-center text-center">
          <div className="relative">
            <div className="h-24 w-24 overflow-hidden rounded-full border-2 border-[#D8E0E8] bg-[#E8EEF3] ring-4 ring-white">
              {photoUrl(member.photo_path) ? (
                <img src={photoUrl(member.photo_path)} alt={member.full_name} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-[#94A3B8]"><User size={40} /></div>
              )}
            </div>
            <label className="absolute -bottom-1 -right-1 cursor-pointer rounded-full bg-[#D4A017] p-1.5 text-white shadow hover:bg-[#B0850F]" data-testid="change-photo-label" style={{ display: canEdit ? undefined : "none" }}>
              <ImagePlus size={14} />
              <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="change-photo-input" />
            </label>
          </div>
          <h2 className="mt-3 font-serif text-2xl font-semibold text-[#082D4A]">{member.full_name}</h2>
          {member.nickname && <p className="text-sm italic text-[#D4A017]">“{member.nickname}”</p>}
          {years(member) && (
            <p className="flex items-center justify-center gap-1.5 text-sm text-[#64748B]">
              {years(member)}
              {(isEstimatedDate(member.birth_date) || isEstimatedDate(member.death_date)) && (
                <span className="rounded-full bg-[#E8EEF3] px-1.5 py-0.5 text-[10px] font-medium text-[#64748B]" data-testid="member-estimated-tag">estimated</span>
              )}
            </p>
          )}
          {canEdit && onSetHome && (
            member.id === homeId ? (
              <button onClick={() => onSetHome(null)} data-testid="sheet-clear-home-button" className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#082D4A] px-3 py-1 text-xs font-semibold text-white hover:bg-[#08223A]"><Home size={12} /> This is you · tap to unset</button>
            ) : (
              <button onClick={() => onSetHome(member.id)} data-testid="sheet-set-home-button" className="mt-2 inline-flex items-center gap-1 rounded-full border border-[#D8E0E8] px-3 py-1 text-xs font-medium text-[#082D4A] hover:bg-[#E8EEF3]"><Home size={12} /> This is me</button>
            )
          )}
          {member.gender && (
            <Badge variant="secondary" className="mt-2 bg-[#E8EEF3] capitalize text-[#082D4A]">{member.gender}</Badge>
          )}
          {(member.first_name || member.last_name || member.former_name) && (
            <div className="mt-2 text-xs text-[#64748B]" data-testid="member-name-parts">
              {[member.first_name, member.middle_name, member.last_name].filter(Boolean).join(" ")}
              {member.former_name && <span> · née {member.former_name}</span>}
            </div>
          )}
        </div>

        {!editing && (
          <>
            {canModify && (
            <div className="mt-6 grid grid-cols-4 gap-2">
              {relBtns.map(({ rel, label, icon: Icon, testid }) => (
                <button
                  key={rel}
                  onClick={() => onAddRelative(member, rel)}
                  data-testid={testid}
                  className="flex flex-col items-center gap-1 rounded-xl border border-[#D8E0E8] bg-white py-2 text-xs font-medium text-[#082D4A] transition-colors hover:bg-[#E8EEF3]"
                >
                  <Icon size={18} className="text-[#D4A017]" />
                  {label}
                </button>
              ))}
            </div>
            )}

            {(member.origin_place || member.current_place) && (
              <div className="mt-5 space-y-2">
                {member.origin_place && (
                  <div className="flex items-center gap-2 text-sm text-[#475569]" data-testid="member-origin-display">
                    <MapPin size={15} className="text-[#D4A017]" />
                    <span className="text-[#64748B]">From:</span> {member.origin_place}
                    {member.origin_unresolved && (
                      <span className="flex items-center gap-1 text-xs text-amber-600" data-testid="origin-unresolved" title="Approximate — couldn't place precisely">
                        <AlertTriangle size={12} /> approx
                      </span>
                    )}
                  </div>
                )}
                {member.current_place && (
                  <div className="flex items-center gap-2 text-sm text-[#475569]" data-testid="member-current-display">
                    <Home size={15} className="text-[#082D4A]" />
                    <span className="text-[#64748B]">Lives in:</span> {member.current_place}
                    {member.current_unresolved && (
                      <span className="flex items-center gap-1 text-xs text-amber-600" data-testid="current-unresolved" title="Approximate — couldn't place precisely">
                        <AlertTriangle size={12} /> approx
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {member.bio && (
              <>
                <Separator className="my-5" />
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#475569]">{member.bio}</p>
                {canEdit && <AnalyzeNotesPanel member={member} onChanged={onChanged} onSelect={onSelect} />}
              </>
            )}

            {[["Parents", parents], ["Spouse / Partner", spouses], ["Children", children], ["Siblings", siblings]].map(
              ([label, list]) =>
                list.length > 0 && (
                  <div key={label} className="mt-5">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#64748B]">{label}</div>
                    <div className="flex flex-wrap gap-2">
                      {list.map((p) => (
                        <RelChip key={p.id} person={p} onSelect={onSelect} />
                      ))}
                    </div>
                  </div>
                )
            )}

            <PhotoGallery member={member} canEdit={canEdit} onChanged={onChanged} />

            <Separator className="my-6" />
            {canEdit && stepchildren.length > 0 && (
              <Button
                onClick={adoptStepchildren}
                data-testid="adopt-spouse-children-button"
                variant="outline"
                className="mb-3 w-full border-[#082D4A]/30 text-[#082D4A] hover:bg-[#E8EEF3]"
              >
                <Baby size={16} className="mr-2" /> Adopt {spouses.map((s) => s.full_name).join(" & ") || "spouse"}'s children ({stepchildren.length})
              </Button>
            )}
            <Button
              onClick={() => onPlayStory?.(member)}
              data-testid="play-their-story-button"
              className="mb-3 w-full bg-[#D4A017] text-white hover:bg-[#B0850F]"
            >
              <Clapperboard size={16} className="mr-2" /> Play their story
            </Button>
            <Button
              onClick={sharePerson}
              data-testid="share-person-button"
              variant="outline"
              className="mb-3 w-full border-[#D8E0E8] text-[#082D4A] hover:bg-[#E8EEF3]"
            >
              <Share2 size={16} className="mr-2" /> Share {member.full_name?.split(" ")[0] || "this page"}
            </Button>
            {canSuggest && !canEdit && (() => {
              const missing = [];
              if (!member.photo_path) missing.push({ label: "a photo", action: () => setEditing(true) });
              if (!member.birth_date) missing.push({ label: "a birth year", action: () => setEditing(true) });
              if (parents.length === 0) missing.push({ label: "their parents", action: () => onAddRelative(member, "parent") });
              return (
                <div className="mt-4 rounded-xl border border-[#D4A017]/30 bg-[#D4A017]/5 p-3" data-testid="contributor-hint">
                  <p className="text-xs text-[#64748B]">You’re a <span className="font-semibold text-[#B0850F]">contributor</span> — your additions and edits are sent to an admin for review.</p>
                  {missing.length > 0 && (
                    <div className="mt-2" data-testid="contributor-nudge">
                      <p className="text-xs font-medium text-[#082D4A]">Help fill in {member.full_name?.split(" ")[0]}’s story — still missing:</p>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {missing.map((m, i) => (
                          <button key={m.label} onClick={m.action} data-testid={`nudge-${i}`} className="rounded-full border border-[#D4A017]/40 bg-white px-2.5 py-1 text-xs text-[#B0850F] hover:bg-[#D4A017]/10">+ Suggest {m.label}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="flex gap-3">
              {canModify ? (
              <>
              <Button
                onClick={() => setEditing(true)}
                data-testid="edit-member-button"
                className="flex-1 bg-[#082D4A] text-white hover:bg-[#08223A]"
              >
                <Pencil size={16} className="mr-2" /> {canEdit ? "Edit" : "Suggest an edit"}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid="delete-member-button">
                    <Trash2 size={16} />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="delete-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{canEdit ? `Remove ${member.full_name}?` : `Suggest removing ${member.full_name}?`}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {canEdit
                        ? "This removes them from the tree and unlinks their relationships. Connected relatives are kept."
                        : "An admin will review your suggestion before anyone is removed."}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-button" className="bg-red-600 hover:bg-red-700">
                      {canEdit ? "Delete" : "Send suggestion"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              </>
              ) : (
                <Button onClick={() => onRequireLogin?.()} data-testid="signin-to-edit-button" variant="outline" className="flex-1 border-[#D8E0E8] text-[#082D4A]">
                  <Pencil size={16} className="mr-2" /> Sign in to edit
                </Button>
              )}
            </div>
            {!canModify && (
              <button onClick={() => onRequestContribute?.()} data-testid="sheet-ask-contribute" className="mt-3 w-full text-center text-xs text-[#D4A017] underline-offset-2 hover:underline">
                Not a member yet? Ask to contribute
              </button>
            )}
          </>
        )}

        {editing && (
          <div className="mt-6">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Editing</span>
              {editStatus !== "idle" && (
                <span data-testid="edit-autosave-indicator" className="flex items-center gap-1 text-xs text-[#64748B]">
                  {editStatus === "saving" ? <><Loader2 size={13} className="animate-spin" /> Saving…</> : <><Check size={13} className="text-[#082D4A]" /> Saved</>}
                </span>
              )}
            </div>
            <MemberFormFields form={form} setForm={setForm} />
            <div className="mt-6 flex gap-3">
              <Button onClick={saveEdit} data-testid="save-edit-button" className="flex-1 bg-[#082D4A] text-white hover:bg-[#08223A]">
                {canEdit ? "Done" : "Send suggestion"}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
